import { useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { EdgeHiba } from '../lib/edge.ts';
import { STATUSZ_CIMKE, type LeadStatusz } from '../lib/tipusok.ts';

type GombValtozat = 'elsodleges' | 'masodlagos' | 'veszely' | 'szoveg' | 'halvany';

const GOMB_STILUS: Record<GombValtozat, string> = {
  elsodleges: 'bg-kiemelt text-kiemelt-szoveg hover:bg-kiemelt-hover font-semibold',
  masodlagos: 'bg-transparent text-szoveg border border-vonal-eros hover:bg-felulet',
  halvany: 'bg-kiemelt-halvany text-kiemelt font-semibold hover:bg-felulet-lista',
  veszely: 'bg-hiba-hatter text-hiba border border-hiba hover:brightness-110',
  szoveg: 'bg-transparent text-szoveg-halk hover:text-szoveg hover:bg-felulet-2',
};

export function Gomb({ valtozat = 'elsodleges', toltes = false, className = '', children, disabled, type = 'button', ...tobbi }:
  ButtonHTMLAttributes<HTMLButtonElement> & { valtozat?: GombValtozat; toltes?: boolean }) {
  return (
    <button
      type={type}
      disabled={disabled || toltes}
      aria-busy={toltes || undefined}
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${GOMB_STILUS[valtozat]} ${className}`}
      {...tobbi}
    >
      {toltes && <span aria-hidden className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />}
      {children}
    </button>
  );
}

// Legalább 16px: az iOS Safari kisebb betűs mezőre fókuszáláskor ránagyít, és úgy is marad (mobilon 1rem = 14px).
const MEZO_STILUS = 'w-full min-h-11 rounded-xl border border-vonal bg-hatter-melyen px-3.5 text-[max(16px,1rem)] text-szoveg placeholder:text-szoveg-halk focus:border-kiemelt';

export function Mezo({ label, hiba, suly, className = '', multiline = false, ...tobbi }:
  { label: string; hiba?: string | null; suly?: string; className?: string; multiline?: boolean } &
  (InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>)) {
  const id = useId();
  const leiroId = `${id}-leiro`;
  const leiro = hiba ?? suly;
  const kozos = { id, 'aria-invalid': hiba ? true : undefined, 'aria-describedby': leiro ? leiroId : undefined };
  return (
    <div className={`grid gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm text-szoveg-halk">{label}</label>
      {multiline
        ? <textarea {...kozos} className={`${MEZO_STILUS} min-h-28 py-3`} {...(tobbi as TextareaHTMLAttributes<HTMLTextAreaElement>)} />
        : <input {...kozos} className={MEZO_STILUS} {...(tobbi as InputHTMLAttributes<HTMLInputElement>)} />}
      {leiro && <p id={leiroId} className={`text-sm ${hiba ? 'text-hiba' : 'text-szoveg-halk'}`}>{leiro}</p>}
    </div>
  );
}

export function Valaszto({ label, hiba, className = '', children, ...tobbi }: { label: string; hiba?: string | null; className?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  const hibaId = `${id}-hiba`;
  return (
    <div className={`grid gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm text-szoveg-halk">{label}</label>
      <select id={id} aria-invalid={hiba ? true : undefined} aria-describedby={hiba ? hibaId : undefined} className={MEZO_STILUS} {...tobbi}>{children}</select>
      {hiba && <p id={hibaId} className="text-sm text-hiba">{hiba}</p>}
    </div>
  );
}

export function Kartya({ className = '', children }: { className?: string; children: ReactNode }) {
  return <section className={`min-w-0 rounded-2xl border border-vonal bg-hatter p-4 sm:p-5 ${className}`}>{children}</section>;
}

const JELVENY_STILUS: Record<LeadStatusz, string> = {
  nem_ellenorzott: 'text-szoveg-halk',
  uj: 'text-kiemelt',
  elerve: 'text-sav-kozep',
  kesobb_aktualis: 'text-sav-kozep',
  szemle_egyeztetve: 'text-masodlagos',
  szemle_megtortent: 'text-masodlagos',
  megbizas: 'text-siker',
  eladva: 'text-siker',
  elveszett: 'text-szoveg-halk',
};

/** Állapotjelző: színes pont és szöveg (nem kitöltött címke). */
export function StatuszJelveny({ statusz }: { statusz: LeadStatusz }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm ${JELVENY_STILUS[statusz]}`}>
      <span aria-hidden className={`size-[7px] rounded-full bg-current ${statusz === 'nem_ellenorzott' ? 'opacity-50' : ''}`} />
      {STATUSZ_CIMKE[statusz]}
    </span>
  );
}

/** Főkönyv: egyetlen felület, benne sorok (nem egyforma kártyák rácsa). A sorok közé a hívó tegyen `border-t`-t, vagy használja a `divide-y`-t. */
export function Fokonyv({ className = '', children }: { className?: string; children: ReactNode }) {
  return <div className={`divide-y divide-vonal overflow-hidden rounded-[18px] border border-vonal bg-hatter ${className}`}>{children}</div>;
}

/** Oldalfej: kisbetűs, halk leírással és jobbra igazított műveletekkel. */
export function OldalFej({ cim, leiras, children }: { cim: string; leiras?: ReactNode; children?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
      <div>
        <h1 className="text-2xl font-medium tracking-[-0.025em] md:text-3xl">{cim}</h1>
        {leiras && <p className="mt-1 text-sm text-szoveg-halk">{leiras}</p>}
      </div>
      {children}
    </header>
  );
}

export function Ures({ cim, szoveg, children }: { cim: string; szoveg?: string; children?: ReactNode }) {
  return (
    <div className="grid justify-items-center gap-2 rounded-2xl border border-dashed border-vonal px-5 py-10 text-center">
      <h2 className="text-lg font-medium tracking-[-0.015em]">{cim}</h2>
      {szoveg && <p className="max-w-md text-szoveg-halk">{szoveg}</p>}
      {children}
    </div>
  );
}

export function Betolto() {
  return (
    <div role="status" aria-live="polite" className="grid min-h-40 place-items-center">
      <span className="size-8 animate-spin rounded-full border-2 border-kiemelt border-t-transparent motion-reduce:animate-none" />
      <span className="sr-only">Betöltés…</span>
    </div>
  );
}

const EDGE_SZOVEG: Record<string, string> = {
  bejelentkezes: 'Lejárt a munkamenet, jelentkezz be újra.',
  jogosulatlan: 'Ehhez nincs jogosultságod.',
  mfa: 'Ehhez a művelethez a kétlépcsős azonosítás szükséges.',
  ervenytelen: 'A megadott adatok érvénytelenek.',
  mar_tag: 'Ez a személy már tagja az irodának.',
  nincs: 'A keresett elem nem található.',
  nincs_iroda: 'Az iroda nem található.',
  sajat: 'Ezt a saját fiókodon nem végezheted el.',
  telefon: 'A telefonszám nem érvényes magyar mobilszám.',
  sandbox_cim: 'Tesztüzemmódban csak tesztcímre lehet meghívót küldeni.',
  slug_foglalt: 'Ez a slug már foglalt, válassz másikat.',
  allapot: 'A regisztráció állapota közben megváltozott. Frissítsd az oldalt.',
  halozat: 'Nincs kapcsolat a szerverrel. Ellenőrizd az internetet, és próbáld újra.',
  szerver: 'Szerverhiba történt. Próbáld újra később.',
};

// A hibakódok, amelyeknél az adatbázis saját, felhasználónak szánt magyar üzenetet küld (raise exception).
const FELHASZNALOI_PG_KODOK = new Set(['23514', '22023', '42501', 'P0001', 'P0002']);

export function hibaSzoveg(hiba: unknown): string {
  if (hiba instanceof EdgeHiba) return EDGE_SZOVEG[hiba.kod] ?? EDGE_SZOVEG.szerver!;
  if (hiba && typeof hiba === 'object') {
    const { code, message } = hiba as { code?: unknown; message?: unknown };
    if (typeof code === 'string' && typeof message === 'string') {
      if (FELHASZNALOI_PG_KODOK.has(code)) return message;
      if (code === '23505') return 'Ilyen elem már létezik.';
      if (code === '23503') return 'A művelet hivatkozott adata nem található.';
    }
    if (typeof message === 'string' && /fetch|network/i.test(message)) return EDGE_SZOVEG.halozat!;
  }
  return 'Váratlan hiba történt. Próbáld újra; ha ismétlődik, szólj nekünk.';
}

export function HibaUzenet({ hiba }: { hiba: unknown }) {
  if (!hiba) return null;
  return <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{hibaSzoveg(hiba)}</p>;
}

export function Parbeszed({ nyitva, cim, onBezar, children }: { nyitva: boolean; cim: string; onBezar(): void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const cimId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (nyitva && !d.open) d.showModal();
    if (!nyitva && d.open) d.close();
  }, [nyitva]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={cimId}
      onClose={onBezar}
      onClick={(e) => { if (e.target === ref.current) onBezar(); }}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-2xl border border-vonal-eros bg-hatter p-0 text-szoveg shadow-[var(--arnyek-2)] backdrop:bg-hatter-melyen/80"
    >
      <div className="grid gap-4 p-5">
        <h2 id={cimId} className="text-lg font-medium tracking-[-0.015em]">{cim}</h2>
        {children}
      </div>
    </dialog>
  );
}
